import React, { useState, useEffect } from 'react';
import {
  Box, Button, TextField, Typography, Switch, FormControlLabel, Grid, Snackbar, Alert, Chip, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Card, CardContent, MenuItem, Select, InputLabel, FormControl, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';

const matchOfferOptions = [
  { value: 'no_match', label: 'Ne pas matcher' },
  { value: 'match', label: "S'aligner" },
  { value: 'above_amount', label: 'Au-dessus de (montant)' },
  { value: 'above_percent', label: 'Au-dessus de (%)' },
  { value: 'below_amount', label: 'En-dessous de (montant)' },
  { value: 'below_percent', label: 'En-dessous de (%)' },
];

const defaultParams = {
  snooze: {},
  minimum: { profit: '', margin: '', roi: '' },
  maximum: { profit: '', margin: '', roi: '' },
  matchOffer: {
    prime: { type: 'match', value: '' },
    nonPrime: { type: 'match', value: '' },
    primeNoNextDay: { type: 'match', value: '' }
  },
  buybox: { strategy: 'reduce_by_amount', step: 0.01 },
  onlySeller: false,
  onlyPrimeSeller: false,
  automations: { noOrderDays: '', newRule: '' },
  excludeSellers: [],
  excludeAmazon: false,
  excludeAmazonUK: false
};

export default function RulesPage() {
  const [rule, setRule] = useState({
    rule_name: '',
    is_default: false,
    params: defaultParams
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [rules, setRules] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [excludeInput, setExcludeInput] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    const res = await fetch('/api/rules');
    const data = await res.json();
    setRules(data);
  };

  const handleChange = (section, field, value) => {
    setRule(prev => ({
      ...prev,
      params: {
        ...prev.params,
        [section]: {
          ...prev.params[section],
          [field]: value
        }
      }
    }));
  };

  const handleMatchOfferChange = (field, type, value) => {
    setRule(prev => ({
      ...prev,
      params: {
        ...prev.params,
        matchOffer: {
          ...prev.params.matchOffer,
          [field]: {
            ...prev.params.matchOffer[field],
            [type]: value
          }
        }
      }
    }));
  };

  const handleRootChange = (field, value) => {
    setRule(prev => ({ ...prev, [field]: value }));
  };

  const handleExcludeAdd = () => {
    if (excludeInput.trim()) {
      setRule(prev => ({
        ...prev,
        params: {
          ...prev.params,
          excludeSellers: [...prev.params.excludeSellers, excludeInput.trim()]
        }
      }));
      setExcludeInput('');
    }
  };

  const handleExcludeDelete = (sellerId) => {
    setRule(prev => ({
      ...prev,
      params: {
        ...prev.params,
        excludeSellers: prev.params.excludeSellers.filter(id => id !== sellerId)
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId ? `/api/rules/${editingId}` : '/api/rules';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(rule)
      });
      if (!res.ok) throw new Error('Erreur lors de la sauvegarde de la règle');
      setSnackbarMsg(editingId ? 'Règle modifiée' : 'Règle créée avec succès');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setRule({ rule_name: '', is_default: false, params: defaultParams });
      setEditingId(null);
      setOpenModal(false);
      fetchRules();
    } catch (err) {
      setSnackbarMsg(err.message);
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handleEdit = (r) => {
    setRule({
      rule_name: r.rule_name,
      is_default: r.is_default,
      params: {
        ...defaultParams,
        ...r.params
      }
    });
    setEditingId(r.id);
    setOpenModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette règle ?')) return;
    await fetch(`/api/rules/${id}`, { method: 'DELETE' });
    fetchRules();
  };

  const handleOpenModal = () => {
    setRule({ rule_name: '', is_default: false, params: defaultParams });
    setEditingId(null);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Pour la liste déroulante Automations
  const ruleOptions = rules.map(r => ({ value: r.id, label: r.rule_name }));

  return (
    <Box maxWidth="md" mx="auto" mt={2}>
      <Typography variant="h4" gutterBottom>Règles de repricing</Typography>
      {/* Liste des règles existantes en haut */}
      <Box mb={4}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Par défaut</TableCell>
                <TableCell>Profit min</TableCell>
                <TableCell>Marge min</TableCell>
                <TableCell>ROI min</TableCell>
                <TableCell>Exclusions</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map(r => (
                <TableRow key={r.id}>
                  <TableCell>{r.rule_name}</TableCell>
                  <TableCell>{r.is_default ? 'Oui' : 'Non'}</TableCell>
                  <TableCell>{r.params?.minimum?.profit ?? ''}</TableCell>
                  <TableCell>{r.params?.minimum?.margin ?? ''}</TableCell>
                  <TableCell>{r.params?.minimum?.roi ?? ''}</TableCell>
                  <TableCell>
                    {r.params?.excludeSellers?.map(id => <Chip key={id} label={id} size="small" />)}
                    {r.params?.excludeAmazon && <Chip label="Amazon" size="small" color="warning" />}
                    {r.params?.excludeAmazonUK && <Chip label="Amazon_UK" size="small" color="warning" />}
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleEdit(r)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton onClick={() => handleDelete(r.id)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenModal}>
        Ajouter une règle
      </Button>
      {/* Modal pour le formulaire d'ajout/modification */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? 'Modifier' : 'Ajouter'} une règle</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <TextField
                      label="Nom de la règle"
                      value={rule.rule_name}
                      onChange={e => handleRootChange('rule_name', e.target.value)}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControlLabel
                      control={<Switch checked={rule.is_default} onChange={e => handleRootChange('is_default', e.target.checked)} />}
                      label="Règle par défaut"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            <Tabs value={activeTab} onChange={handleTabChange} centered>
              <Tab label="Minimums/Maximums" />
              <Tab label="Match Offer" />
              <Tab label="BuyBox" />
              <Tab label="Conditions" />
              <Tab label="Exclusions" />
              <Tab label="Automations" />
            </Tabs>
            {activeTab === 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Minimums</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={4}><TextField label="Profit min (€)" type="number" value={rule.params.minimum.profit} onChange={e => handleChange('minimum', 'profit', e.target.value)} fullWidth /></Grid>
                        <Grid item xs={4}><TextField label="Marge min (%)" type="number" value={rule.params.minimum.margin} onChange={e => handleChange('minimum', 'margin', e.target.value)} fullWidth /></Grid>
                        <Grid item xs={4}><TextField label="ROI min (%)" type="number" value={rule.params.minimum.roi} onChange={e => handleChange('minimum', 'roi', e.target.value)} fullWidth /></Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Maximums</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={4}><TextField label="Profit max (€)" type="number" value={rule.params.maximum.profit} onChange={e => handleChange('maximum', 'profit', e.target.value)} fullWidth /></Grid>
                        <Grid item xs={4}><TextField label="Marge max (%)" type="number" value={rule.params.maximum.margin} onChange={e => handleChange('maximum', 'margin', e.target.value)} fullWidth /></Grid>
                        <Grid item xs={4}><TextField label="ROI max (%)" type="number" value={rule.params.maximum.roi} onChange={e => handleChange('maximum', 'roi', e.target.value)} fullWidth /></Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
            {activeTab === 1 && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Match Offer</Typography>
                  <Grid container spacing={2}>
                    {['prime', 'primeNoNextDay', 'nonPrime'].map(field => (
                      <Grid item xs={12} md={4} key={field}>
                        <FormControl fullWidth>
                          <InputLabel>{field === 'prime' ? 'Prime' : field === 'primeNoNextDay' ? 'Prime sans next day' : 'Non-Prime'}</InputLabel>
                          <Select
                            value={rule.params.matchOffer[field].type}
                            label={field === 'prime' ? 'Prime' : field === 'primeNoNextDay' ? 'Prime sans next day' : 'Non-Prime'}
                            onChange={e => handleMatchOfferChange(field, 'type', e.target.value)}
                          >
                            {matchOfferOptions.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {(rule.params.matchOffer[field].type === 'above_amount' || rule.params.matchOffer[field].type === 'above_percent' || rule.params.matchOffer[field].type === 'below_amount' || rule.params.matchOffer[field].type === 'below_percent') && (
                          <TextField
                            label={rule.params.matchOffer[field].type.includes('amount') ? 'Montant (€)' : 'Pourcentage (%)'}
                            type="number"
                            value={rule.params.matchOffer[field].value}
                            onChange={e => handleMatchOfferChange(field, 'value', e.target.value)}
                            fullWidth
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            )}
            {activeTab === 2 && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>BuyBox</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={8}><TextField label="Stratégie" value={rule.params.buybox.strategy} onChange={e => handleChange('buybox', 'strategy', e.target.value)} fullWidth /></Grid>
                    <Grid item xs={4}><TextField label="Step (€)" type="number" value={rule.params.buybox.step} onChange={e => handleChange('buybox', 'step', e.target.value)} fullWidth /></Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}
            {activeTab === 3 && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Conditions d'application</Typography>
                  <Tooltip title="N'appliquer la règle que si vous êtes le seul vendeur sur l'offre" arrow>
                    <FormControlLabel control={<Switch checked={rule.params.onlySeller} onChange={e => handleChange('onlySeller', '', e.target.checked)} />} label="Seulement si seul vendeur" />
                  </Tooltip>
                  <Tooltip title="N'appliquer la règle que si vous êtes le seul vendeur Prime" arrow>
                    <FormControlLabel control={<Switch checked={rule.params.onlyPrimeSeller} onChange={e => handleChange('onlyPrimeSeller', '', e.target.checked)} />} label="Seulement si seul Prime" />
                  </Tooltip>
                </CardContent>
              </Card>
            )}
            {activeTab === 4 && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Exclusions vendeurs</Typography>
                  <FormControlLabel control={<Switch checked={rule.params.excludeAmazon} onChange={e => handleChange('excludeAmazon', '', e.target.checked)} />} label="Exclure Amazon" />
                  <FormControlLabel control={<Switch checked={rule.params.excludeAmazonUK} onChange={e => handleChange('excludeAmazonUK', '', e.target.checked)} />} label="Exclure Amazon_UK" />
                  <Box mt={2} display="flex" gap={2} alignItems="center">
                    <TextField label="Ajouter un Seller ID à exclure" value={excludeInput} onChange={e => setExcludeInput(e.target.value)} size="small" />
                    <Button variant="outlined" onClick={handleExcludeAdd}>Ajouter</Button>
                  </Box>
                  <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                    {rule.params.excludeSellers.map(id => (
                      <Chip key={id} label={id} onDelete={() => handleExcludeDelete(id)} />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}
            {activeTab === 5 && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Automations</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}><TextField label="Si pas de vente depuis (jours)" type="number" value={rule.params.automations.noOrderDays} onChange={e => handleChange('automations', 'noOrderDays', e.target.value)} fullWidth /></Grid>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <InputLabel>Basculer sur la règle</InputLabel>
                        <Select
                          value={rule.params.automations.newRule}
                          label="Basculer sur la règle"
                          onChange={e => handleChange('automations', 'newRule', e.target.value)}
                        >
                          <MenuItem value="">Aucune</MenuItem>
                          {ruleOptions.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Annuler</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">{editingId ? 'Modifier' : 'Enregistrer'} la règle</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={openSnackbar} autoHideDuration={4000} onClose={() => setOpenSnackbar(false)}>
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
} 